/** Synchronizacja z SaleLight (wymaga trybu ONLINE):
 * 1. dosłanie niezsynchronizowanych transakcji (`/osdm/offline-sync`) — łańcuch
 *    hashy jedzie w paczce, serwer weryfikuje ciągłość od ostatniego ogniwa;
 * 2. stan urządzenia (`/osdm/devices/{id}/offline-state`): ważność puli numerów
 *    i przydziały limitów; przy okazji MELDUNEK o liczbie
 *    transakcji czekających na dosłanie — operator ma widzieć kasę z zaległą
 *    sprzedażą, zanim ta sprzedaż stanie się problemem rozliczeniowym;
 * 3. uzupełnienie puli numerów, gdy zapas < 20% albo pula straciła ważność;
 * 4. kompilat taryfy po ETagu (304 = mamy aktualny).
 * Kolejność celowa: NAJPIERW dosyłamy (rozliczenia), potem bierzemy nowe dane. */

import { kv, txns } from "./db";
import { decryptJson, isUnlocked } from "./vault";
import { config, online } from "./api";
import { DevicePool, LimitAllocation, poolCheck, Rejection } from "./policy";
import { Pool, poolState } from "./sale";

/** Jeden z trzech etapów widocznych w ReadinessBar (ten sam etap co w §2a–2c
 * handoffu designu). „Stan urządzenia" (limity/przydziały) nie jest tu liczony —
 * był fail-soft od początku i nie blokuje sprzedaży, więc nie jest jedną z liczb
 * w „{k} z 3 etapów udane". */
export type SyncStageKey = "transakcje" | "pula" | "taryfa";
export interface SyncStageResult { key: SyncStageKey; status: "ok" | "error"; detail?: string; }

export interface SyncReport {
  sent: number; duplicates: number; poolTopUp: boolean; bundleRefreshed: boolean;
  bundleHash?: string; errors: string[];
  /** Wynik KAŻDEGO z trzech etapów, nawet gdy wcześniejszy zawiódł — etapy są
   * od siebie niezależne (różne wywołania `online()`), więc porażka jednego nie
   * jest powodem, żeby nie spróbować pozostałych. Na końcu funkcji nadal rzuca
   * PIERWSZY napotkany błąd (zgodność z dotychczasowymi wołającymi) — to pole
   * jest dodatkiem, nie zmienia tego, co widzi stary kod. Ponawianie POJEDYNCZEGO
   * etapu to osobna, nierozstrzygnięta decyzja (handoff designu §10) — tu tylko
   * raportujemy, nie ponawiamy selektywnie. */
  stages: SyncStageResult[];
}

const POOL_SIZE = 100;
const POOL_LOW_WATERMARK = 0.2;

/** Stan urządzenia po stronie SaleLighta: ważność puli i przydziały limitów.
 * Progu wieku pakietu TU NIE MA celowo — jest ustawieniem kanału i przychodzi
 * w pakiecie (`bundle.offline_policy`); dwa źródła tej samej liczby rozjechałyby
 * się przy pierwszej zmianie, a decyduje ona o wstrzymaniu sprzedaży. */
interface OfflineState {
  pool?: Partial<DevicePool> | null;
  limitAllocations?: LimitAllocation[] | null;
}

/** Odrzucenie paczki zapisane przy transakcjach i w stanie urządzenia.
 * Transakcji NIE kasujemy ani nie oznaczamy jako wysłanych — sprzedaż się
 * odbyła, bilety są u podróżnych, a jedyne, co wolno zrobić, to pokazać powód
 * i czekać na uzgodnienie. */
async function recordRejection(
  pending: { local_uuid: string }[], reason: string,
): Promise<void> {
  const at = new Date().toISOString();
  const r: Rejection = { at, reason, transactions: pending.length };
  await kv.set("last_rejection", r);
  const ids = new Set(pending.map((t) => t.local_uuid));
  for (const row of await txns.all()) {
    if (!ids.has(row.local_uuid) || row.synced) continue;
    row.rejected_at = at;
    row.rejected_reason = reason;
    await txns.put(row);
  }
}

export async function syncAll(): Promise<SyncReport> {
  const report: SyncReport = { sent: 0, duplicates: 0, poolTopUp: false,
    bundleRefreshed: false, errors: [], stages: [] };
  const cfg = await config();
  if (!cfg) throw new Error("urządzenie nieskonfigurowane");
  if (!isUnlocked()) throw new Error("magazyn zablokowany — podaj PIN przed synchronizacją");

  // Każdy etap jest NIEZALEŻNY (osobne wywołanie `online()`), więc porażka
  // jednego nie jest powodem, żeby pominąć pozostałe — inaczej jeden zerwany
  // request chowałby informację o dwóch pozostałych etapach. Pierwszy napotkany
  // błąd zostaje zapamiętany i rzucony NA KOŃCU, żeby dotychczasowi wołający
  // (np. `doSync()`) nie zauważyli zmiany zachowania.
  let pierwszyBlad: Error | null = null;

  // ── 1. transakcje ──
  try {
    const all = (await txns.all()).sort((a, b) => a.created_at.localeCompare(b.created_at));
    const pending = all.filter((t) => !t.synced);
    if (pending.length) {
      const transactions = [];
      for (const t of pending) transactions.push(await decryptJson(t.envelope));
      let resp;
      try {
        resp = await online<{ results: { localUuid: string; duplicate: boolean }[] }>(
          "/osdm/offline-sync", { body: { device_id: cfg.deviceId, transactions,
            // ile transakcji urządzenie ma u siebie — serwer nie ma skąd tego
            // wiedzieć, a to jedyna liczba mówiąca „ta kasa ma zaległą sprzedaż"
            pending_count: pending.length } });
      } catch (e) {
        await recordRejection(pending, (e as Error).message);
        throw e;
      }
      for (const r of resp.data!.results) {
        const row = pending.find((t) => t.local_uuid === r.localUuid);
        if (row) {
          row.synced = true;
          delete row.rejected_at;
          delete row.rejected_reason;
          await txns.put(row);
        }
        r.duplicate ? report.duplicates++ : report.sent++;
      }
      await kv.del("last_rejection");
    }
    report.stages.push({ key: "transakcje", status: "ok" });
  } catch (e) {
    const msg = (e as Error).message;
    report.stages.push({ key: "transakcje", status: "error", detail: msg });
    pierwszyBlad ??= e as Error;
  }

  // ── 2. stan urządzenia: ważność puli numerów i przydziały limitów ──
  // Fail-soft: serwer bez tej trasy (starsze wdrożenie) nie może zatrzymać
  // synchronizacji rozliczeń — kasa pracuje wtedy jak dotąd, a brak przydziałów
  // sam z siebie zamyka sprzedaż ofert objętych limitem wspólnym.
  try {
    const st = await online<OfflineState>(
      `/osdm/devices/${cfg.deviceId}/offline-state`,
      { body: { pending_count: (await txns.all()).filter((t) => !t.synced).length } });
    const d = st.data;
    if (d) {
      if (d.limitAllocations !== undefined)
        await kv.set("limit_allocations", d.limitAllocations ?? []);
      const lokalna = await poolState();
      if (d.pool && lokalna && d.pool.series === lokalna.series
          && d.pool.from === lokalna.from && d.pool.to === lokalna.to)
        // termin i unieważnienie nadaje operator PO wydaniu zakresu, więc
        // przepisujemy je na pulę, którą urządzenie już ma
        await kv.set("pool", { ...lokalna, expires_at: d.pool.expires_at ?? null,
          revoked_at: d.pool.revoked_at ?? null });
    }
  } catch (e) {
    report.errors.push(`stan urządzenia: ${(e as Error).message}`);
  }

  // ── 3. pula numerów ──
  try {
    const pool = await poolState();
    const stan = poolCheck(pool, new Date());
    const capacity = pool ? pool.to - pool.from + 1 : 0;
    // nowy zakres bierzemy nie tylko przy niskim zapasie, ale też gdy obecny
    // stracił ważność albo został unieważniony — inaczej kasa odjeżdża z pulą,
    // z której żaden bilet nie zostanie przyjęty
    if (!stan.ok || stan.remaining < capacity * POOL_LOW_WATERMARK) {
      const r = await online<{ series: string; from: number; to: number;
        expires_at?: string | null }>(
        `/osdm/devices/${cfg.deviceId}/number-pool`, { body: { count: POOL_SIZE } });
      const fresh: Pool = { ...r.data!, next: r.data!.from,
        expires_at: r.data!.expires_at ?? null, revoked_at: null };
      // resztka starej puli PRZEPADA (dziura w numeracji jest tańsza niż dwa
      // równoległe zakresy i ryzyko pomyłki przy wyborze)
      await kv.set("pool", fresh);
      report.poolTopUp = true;
    }
    report.stages.push({ key: "pula", status: "ok" });
  } catch (e) {
    const msg = (e as Error).message;
    report.stages.push({ key: "pula", status: "error", detail: msg });
    pierwszyBlad ??= e as Error;
  }

  // ── 4. kompilat taryfy ──
  try {
    const etag = await kv.get<string>("bundle_etag");
    const r = await online<Record<string, unknown>>("/osdm/offline-bundle", { etag });
    if (r.status === 200 && r.data) {
      await kv.set("bundle", r.data);
      await kv.set("bundle_etag", r.etag ?? "");
      report.bundleRefreshed = true;
      report.bundleHash = String(r.data.bundle_hash ?? "");
    }
    report.stages.push({ key: "taryfa", status: "ok" });
  } catch (e) {
    const msg = (e as Error).message;
    report.stages.push({ key: "taryfa", status: "error", detail: msg });
    pierwszyBlad ??= e as Error;
  }

  await kv.set("last_sync_at", new Date().toISOString());
  await kv.set("last_sync_stages", report.stages);
  if (pierwszyBlad) throw pierwszyBlad;
  return report;
}

export async function pendingCount(): Promise<number> {
  return (await txns.all()).filter((t) => !t.synced).length;
}

/** Wynik OSTATNIEJ synchronizacji per etap — przetrwa nawet gdy `syncAll()`
 * ostatecznie rzuciła (zapisujemy PRZED ewentualnym rzuceniem), bo ReadinessBar
 * ma pokazać „{k} z 3 etapów udane", nie tylko „synchronizacja nieudana". */
export async function lastSyncStages(): Promise<SyncStageResult[]> {
  return (await kv.get<SyncStageResult[]>("last_sync_stages")) ?? [];
}

export async function lastSyncAt(): Promise<string | null> {
  return (await kv.get<string>("last_sync_at")) ?? null;
}

/** Meldunek o zaległej sprzedaży BEZ pełnej synchronizacji.
 *
 * Pełna synchronizacja wymaga odblokowanego magazynu (PIN) i jest czynnością
 * kasjera; odzyskanie łączności nią nie jest. Bez tego meldunku operator dowiaduje
 * się o kasie z niedosłaną sprzedażą dopiero wtedy, gdy kasjer sam naciśnie
 * „Synchronizuj" — czyli nigdy, jeśli tego nie zrobi. Cicho i fail-soft: to
 * powiadomienie, nie operacja rozliczeniowa. */
export async function reportPending(): Promise<void> {
  try {
    const cfg = await config();
    if (!cfg) return;
    const pending = (await txns.all()).filter((t) => !t.synced).length;
    await online(`/osdm/devices/${cfg.deviceId}/offline-state`,
      { body: { pending_count: pending } });
  } catch {
    // brak trasy u starszego serwera albo zerwana łączność — meldunek jest
    // dodatkiem, nie może wywrócić paska stanu
  }
}

/** Ostatnie odrzucenie paczki — do paska stanu i do uzgodnienia z operatorem. */
export async function lastRejection(): Promise<Rejection | null> {
  return (await kv.get<Rejection>("last_rejection")) ?? null;
}
